import React, { FC, useMemo, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useRouter } from "next/router";
import { useToast } from "@src/lib/hooks/useToast";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import { CONTENT_LIBRARY_REFETCH_QUERIES } from "@src/lib/contexts/TagContext/TagContext";
import ArchiveConfirmationDialog from "@src/features/contentLibrary/dialogs/ArchiveConfirmationDialog";
import {
  Ff,
  GraphqlOperations,
  PublishState,
  TrainingResourceVersionType,
} from "@src/types.generated";
import { useTracking } from "@src/features/app/hooks/useTracking";
import { TrainingResourceLibraryItem } from "./TrainingResourceDetailPage";
import {
  currentStateIsPublishedAtom,
  selectedTrainingResourceIdAtom,
  trainingResourceTitleAtom,
} from "./atoms";
import { useLibraryItemQueryParams } from "../contexts/LibraryItemQueryParamsContext";
import { cn } from "@src/lib/utils/cn";
import { openDownload } from "@src/lib/utils/csv";
import { launchConfetti } from "@src/lib/utils/confetti";
import { useArchiveTrainingResourceMutation } from "@src/features/libraryItem/LibraryItemDetailPageContainer.generated";
import ArchiveOutlineIcon from "@src/lib/icons/18px/archive-outline";
import { DropdownMenuItem } from "@src/lib/ui/DropdownMenu";
import ArrowRotateCounterclockwiseIcon from "@src/lib/icons/18px/arrow-rotate-counterclockwise";
import FileArrowInIcon from "@src/lib/icons/18px/file-arrow-in";
import GearOutlineIcon from "@src/lib/icons/18px/gear-outline";
import { Button } from "@src/lib/ui/Button";
import ArrowDownloadIcon from "@src/lib/icons/18px/arrow-download";
import { usePublishTrainingResourceMutation } from "./TrainingResourcePageHeader.generated";
import EditLibraryItemDialog, {
  EditLibraryItemDialogData,
} from "../module/EditLibraryItemDialog";
import { updateTranslationSet } from "@src/features/contentDetail/utils/translationSets";
import useTranslationModeContext from "@src/features/libraryItem/course/hooks/useTranslationModeContext";
import { useAvailableLanguages } from "@src/features/contentDetail/hooks/useAvailableLanguages";
import LibraryItemPublishStatePill from "../shared/LibraryItemPublishStatePill";
import useContentPermissions from "../hooks/useContentPermissions";
import { ContentPermissionsFragment } from "../hooks/useContentPermissions.generated";
import { useAtomValue } from "jotai";
import { TrainingResourceVersionFragment } from "@src/fragments.generated";
import { useUpdateTrainingResourceMutation } from "@src/features/contentLibrary/dialogs/TrainingResourceDialog.generated";
import { gql } from "@apollo/client";
import { moduleVersionModuleIdAtom } from "../module/atoms";
import TrainingResourceVersionHistoryDropdown from "@src/features/libraryItem/resource/TrainingResourceVersionHistoryDropdown";
import LockedTrainingResourceVersionBanner from "@src/features/libraryItem/resource/LockedTrainingResourceVersionBanner";
import useVersionQueryParams from "@src/features/libraryItem/hooks/useVersionQueryParams";
import { usePortalTarget } from "@src/lib/hooks/usePortalTarget";
import LibraryItemBuilderTitleExtra from "../shared/LibraryItemBuilderTitleExtra";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import AgentActivityIndicator from "@src/features/libraryItem/agentActivity/AgentActivityIndicator";
import { isMediaConversionInProgress } from "./mediaConversion";
import BuilderMainTabsNav from "@src/features/app/top-nav/BuilderMainTabsNav";
import LibraryItemDetailPageHeaderRight from "../LibraryItemDetailPageHeaderRight";
import ShareTrainingResourceDialog from "@src/features/libraryItem/course/shareButton/ShareTrainingResourceDialog";
import { useBuilderSavingIndicator } from "@src/features/builders/shared/autosave/BuilderSavingIndicator";
import BuilderActionSyncingErrorBanner from "@src/features/builders/shared/autosave/BuilderActionSyncingErrorBanner";
import { useSyncBuilderActionsStore } from "@src/features/libraryItem/hooks/useSyncBuilderActionsToServer";

type Props = {
  libraryItem: TrainingResourceLibraryItem;
  trainingResourceVersion?: TrainingResourceVersionFragment;
};

export const useTrainingResourceContentPermissions = ():
  | ContentPermissionsFragment
  | undefined => {
  const trainingResourceId = useAtomValue(selectedTrainingResourceIdAtom);
  return useContentPermissions({
    trainingResourceId,
  });
};

const TrainingResourcePageHeader: FC<Props> = (props) => {
  const router = useRouter();
  const { setManageTab, selectedMainTab, mainTabs, setMainTab } =
    useLibraryItemQueryParams();
  const { version } = useVersionQueryParams();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const { userActionPermissions } = useActionPermissions();
  const translationModeContext = useTranslationModeContext();
  const { trackEvent } = useTracking();
  const { addErrorToast, addToast } = useToast();
  const currentStateIsPublished = useAtomValue(currentStateIsPublishedAtom);
  const titlePortal = usePortalTarget("top-nav-page-title-extra");
  const actionsPortal = usePortalTarget("top-nav-actions");
  const mainTabsPortal = usePortalTarget("top-nav-main-tabs");
  const [archiveTrainingResource] = useArchiveTrainingResourceMutation({
    refetchQueries: CONTENT_LIBRARY_REFETCH_QUERIES,
    onCompleted: (data) => {
      if (!data.archiveTrainingResource.success) {
        addErrorToast({
          ...data.archiveTrainingResource,
          callsite: "drag_training_resource_archive_training_resource",
        });
      }
    },
  });
  const { allLanguages: langs } = useAvailableLanguages();
  const [updateTrainingResource] = useUpdateTrainingResourceMutation({
    refetchQueries: [GraphqlOperations.Query.LibraryItemDetailPage_LibraryItem],
  });
  const trainingResourceId = props.libraryItem.trainingResource?.id;

  const trainingResourceTitle = useAtomValue(trainingResourceTitleAtom);
  const displayTitle = trainingResourceTitle?.en || "Untitled Resource";

  const onSave = useCallback(
    (data: EditLibraryItemDialogData) => {
      if (!translationModeContext) {
        throw new Error("No translation mode context");
      }
      updateTrainingResource({
        variables: {
          trainingResourceId: props.libraryItem.trainingResource?.id,
          input: {
            name: updateTranslationSet({
              lang: translationModeContext.currentLang,
              text: data.name,
              ts: props.libraryItem.name,
            }),
            description: updateTranslationSet({
              lang: translationModeContext.currentLang,
              text: data.description,
              ts: props.libraryItem.description,
            }),
          },
        },
      });
    },
    [
      props.libraryItem.description,
      props.libraryItem.name,
      props.libraryItem.trainingResource?.id,
      translationModeContext,
      updateTrainingResource,
    ],
  );
  const permissions = useTrainingResourceContentPermissions();
  const canEditTitle = Boolean(permissions?.hasEditContentPermission);
  const onShareClick = useCallback(() => {
    if (!trainingResourceId) return;
    setShareDialogOpen(true);
  }, [trainingResourceId]);
  const goToSettingsTab = useCallback(() => {
    setManageTab("settings");
  }, [setManageTab]);

  const handleExportPDF = useCallback(() => {
    if (!trainingResourceId) return;
    const params = new URLSearchParams();
    if (translationModeContext?.currentLang) {
      params.set("lang", translationModeContext.currentLang);
    }
    if (version) {
      params.set("version", version);
    }
    const qs = params.toString();
    window.open(
      `/training-resource/${trainingResourceId}/export${qs ? `?${qs}` : ""}`,
      "_blank",
    );
  }, [trainingResourceId, translationModeContext?.currentLang, version]);

  const handleDownloadMedia = useCallback(() => {
    const mediaUrl =
      props.libraryItem.trainingResource?.publishedVersion?.media
        ?.unoptimizedUrl ||
      props.libraryItem.trainingResource?.publishedVersion?.media?.mediaUrls.en;
    if (!mediaUrl) {
      return;
    }
    openDownload(mediaUrl, props.libraryItem.name.en);
    trackEvent("download_training_resource", {
      id: props.libraryItem.trainingResource.id,
    });
  }, [
    props.libraryItem.name.en,
    props.libraryItem.trainingResource,
    trackEvent,
  ]);

  const handleDownloadOrExport = useCallback(() => {
    const isRichTextDocument =
      props.libraryItem.trainingResource.draftVersion.richTextDocument;

    isRichTextDocument ? handleExportPDF() : handleDownloadMedia();
  }, [
    props.libraryItem.trainingResource.draftVersion.richTextDocument,
    handleExportPDF,
    handleDownloadMedia,
  ]);

  const [publishTrainingResource] = usePublishTrainingResourceMutation({
    refetchQueries: [GraphqlOperations.Query.LibraryItemDetailPage_LibraryItem],
    onCompleted: (data) => {
      if (data.publishTrainingResource.success) {
        launchConfetti();
        addToast({ message: "Resource published successfully" });
      } else {
        addErrorToast({
          ...data,
          message: "Failed to publish resource",
          callsite: "publish_training_resource",
        });
      }
    },
    onError: (error) => {
      addErrorToast({
        ...error,
        message: "Failed to publish resource",
        callsite: "publish_training_resource",
      });
    },
  });
  const handlePublishTrainingResource = useCallback(() => {
    if (
      !trainingResourceId ||
      !props.libraryItem.trainingResource.draftVersion?.id ||
      !permissions?.hasPublishPermission
    ) {
      addErrorToast({
        message: "You do not have permission to publish this resource",
        callsite: "publish_training_resource",
      });
      return;
    }
    publishTrainingResource({
      variables: {
        trainingResourceId,
      },
    });
  }, [
    addErrorToast,
    permissions?.hasPublishPermission,
    publishTrainingResource,
    trainingResourceId,
    props.libraryItem.trainingResource.draftVersion?.id,
  ]);
  const publishState: PublishState = useMemo(() => {
    return (
      props.trainingResourceVersion?.trainingResource?.libraryItem
        ?.publishState ?? PublishState.Draft
    );
  }, [
    props.trainingResourceVersion?.trainingResource?.libraryItem?.publishState,
  ]);
  const docConversionAgentEnabled = useFeatureFlag(
    Ff.ProductContainerDocConversion,
  );
  // Not memoized: reference-based deps can miss nested status changes. Flag
  // gated: legacy Gemini conversions are in progress too, but no agent is
  // working on them.
  const isAgentConversionInProgress =
    docConversionAgentEnabled &&
    isMediaConversionInProgress(
      props.trainingResourceVersion?.media
        ?.mostRecentConvertMediaToRichTextDocumentWorkflow,
    );
  const lastSyncAt = useSyncBuilderActionsStore((s) => s.lastSuccessfulSyncAt);
  const queue = useSyncBuilderActionsStore((s) => s.queue);
  const firstSyncErrorAt = useSyncBuilderActionsStore(
    (s) => s.firstSyncErrorAt,
  );
  const savingIndicator = useBuilderSavingIndicator({
    queueLength: queue.length,
    lastSuccessfulSyncAt: lastSyncAt,
  });
  const inModuleBuilder = !!useAtomValue(moduleVersionModuleIdAtom);
  const shouldHidePublishButton = useMemo(() => {
    if (inModuleBuilder) {
      return false;
    }
    return (
      props.trainingResourceVersion?.type ===
        TrainingResourceVersionType.Media &&
      props.trainingResourceVersion?.trainingResource?.libraryItem
        ?.publishState === PublishState.Published
    );
  }, [
    inModuleBuilder,
    props.trainingResourceVersion?.trainingResource?.libraryItem?.publishState,
    props.trainingResourceVersion?.type,
  ]);

  const extraButtons =
    selectedMainTab === "build" ? (
      <div className="flex items-center gap-2">
        <TrainingResourceVersionHistoryDropdown
          trainingResourceVersion={props.trainingResourceVersion}
        />
        <Button variant="ghost" onClick={handleDownloadOrExport}>
          <ArrowDownloadIcon />
          Download
        </Button>
      </div>
    ) : selectedMainTab === "preview" ? (
      <Button variant="ghost" onClick={handleDownloadOrExport}>
        <ArrowDownloadIcon />
        Download
      </Button>
    ) : null;

  const overflowItems = (
    <>
      <DropdownMenuItem onClick={goToSettingsTab}>
        <GearOutlineIcon className="text-muted-foreground" />
        Settings
      </DropdownMenuItem>
      {trainingResourceId && (
        <DropdownMenuItem onClick={handleExportPDF}>
          <FileArrowInIcon className="text-muted-foreground" />
          Export PDF
        </DropdownMenuItem>
      )}
      {userActionPermissions?.manageLibrary &&
        (props.libraryItem.archivedAt ? (
          <DropdownMenuItem
            onClick={() => {
              archiveTrainingResource({
                variables: {
                  id: props.libraryItem.trainingResource.id,
                  archive: false,
                },
              });
            }}
          >
            <ArrowRotateCounterclockwiseIcon className="text-muted-foreground" />
            Unarchive
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onClick={() => {
              setArchiveOpen(true);
            }}
          >
            <ArchiveOutlineIcon className="text-muted-foreground" />
            Archive
          </DropdownMenuItem>
        ))}
    </>
  );

  const titleEl = titlePortal
    ? createPortal(
        <LibraryItemBuilderTitleExtra
          libraryItemType={props.libraryItem.type}
          title={
            <button
              type="button"
              disabled={!canEditTitle}
              onClick={() => {
                if (canEditTitle) {
                  setIsEditDialogOpen(true);
                }
              }}
              className={cn(
                "line-clamp-2 max-w-[420px] rounded px-1.5 text-left text-sm font-semibold tracking-[-0.32px] text-foreground",
                canEditTitle && "hover:bg-gray-3",
              )}
              title={displayTitle}
            >
              {displayTitle}
            </button>
          }
          status={
            publishState ? (
              <>
                <LibraryItemPublishStatePill
                  publishState={publishState}
                  archivedAt={props.libraryItem.archivedAt}
                />
                <AgentActivityIndicator
                  active={isAgentConversionInProgress}
                  detail="Converting document"
                />
                {savingIndicator}
              </>
            ) : undefined
          }
        />,
        titlePortal,
      )
    : null;

  const tabsEl =
    mainTabsPortal && mainTabs.length > 0
      ? createPortal(
          <BuilderMainTabsNav
            tabs={mainTabs}
            value={selectedMainTab}
            onValueChange={setMainTab}
          />,
          mainTabsPortal,
        )
      : null;

  const actionsEl = actionsPortal
    ? createPortal(
        <LibraryItemDetailPageHeaderRight
          selectedMainTab={selectedMainTab}
          translationModeEnabled={
            translationModeContext?.translationModeEnabled
          }
          setTranslationModeEnabled={
            langs.length > 1
              ? translationModeContext.setTranslationModeEnabled
              : undefined
          }
          hasPublishPermission={!!permissions?.hasPublishPermission}
          onPublishClick={handlePublishTrainingResource}
          publishButtonText="Publish"
          publishDisabled={currentStateIsPublished || version === "published"}
          extraButtons={extraButtons}
          overflowItems={overflowItems}
          hidePublishButton={shouldHidePublishButton}
          onShareClick={
            permissions?.hasShareWithIndividualsPermission ||
            permissions?.hasEditAdminAndManagerSharingPermission
              ? onShareClick
              : undefined
          }
        />,
        actionsPortal,
      )
    : null;

  return (
    <div className="flex flex-col self-stretch">
      <BuilderActionSyncingErrorBanner firstSyncErrorAt={firstSyncErrorAt} />
      {titleEl}
      {tabsEl}
      {actionsEl}
      <LockedTrainingResourceVersionBanner />
      {isEditDialogOpen && (
        <EditLibraryItemDialog
          open={isEditDialogOpen}
          onOpenChange={setIsEditDialogOpen}
          title="Edit Resource"
          name={displayTitle}
          description={props.libraryItem.description.en}
          coverImage={props.libraryItem.coverImage}
          onSave={onSave}
          disableCoverImageEditing
        />
      )}
      {shareDialogOpen && trainingResourceId && (
        <ShareTrainingResourceDialog
          open={shareDialogOpen}
          onOpenChange={setShareDialogOpen}
          trainingResourceId={trainingResourceId}
        />
      )}
      {archiveOpen && (
        <ArchiveConfirmationDialog
          open={archiveOpen}
          onOpenChange={setArchiveOpen}
          onArchive={() => {
            archiveTrainingResource({
              variables: {
                id: props.libraryItem.trainingResource.id,
                archive: true,
              },
            }).then((result) => {
              if (result.data?.archiveTrainingResource.success) {
                addToast({
                  message: "Resource successfully archived 🎉",
                });
                // Return to the library: the user was viewing the item they
                // just archived, so staying on its page strands them
                // (OPUS-17039)
                router.push({ pathname: "/library" });
              }
            });
          }}
          title={"Archive Resource"}
          itemName={props.libraryItem.name.en}
          includeModuleCopy
        />
      )}
    </div>
  );
};

export default TrainingResourcePageHeader;

gql`
  mutation PublishTrainingResource($trainingResourceId: UUID!) {
    publishTrainingResource(trainingResourceId: $trainingResourceId) {
      trainingResource {
        ...TrainingResourceBuilder
      }
      success
    }
  }
  mutation UpdateTrainingResourceNameAndDescription(
    $trainingResourceId: String!
    $input: UpdateTrainingResourceInput!
  ) {
    updateTrainingResource(
      trainingResourceId: $trainingResourceId
      input: $input
    ) {
      success
    }
  }
`;
